import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { ChevronRight, FileText, Info, Lock, MessageSquareWarning, Network, Star } from "lucide-react";
import { clientRoutes } from "@/lib/clientRoutes";
import { cn } from "@/lib/cn";
import { uah } from "@/lib/clientFormat";
import { legalSurfaceLabel } from "@/lib/client360Labels";
import { useClientTab, useClient360Contacts } from "@/hooks/useClients";
import { ContactDrawer } from "@/components/clients/ContactDrawer";
import { LegalEntityDetailPane } from "./LegalEntityDetail";
import type {
  Client360Envelope,
  ClientActivity,
  ClientComplaint,
  ClientContract,
  ClientLegalEntity,
} from "@/api/clients";

/**
 * A-Block A2 — lazy tab panels for Client360 (AC-C360-11 compact + lazy).
 * Each renders as a nested route child of /clients/:holdingId and fetches its
 * own endpoint on mount via useClientTab. Envelope comes from <Outlet context>.
 */

function useHoldingId(): string {
  const { holdingId } = useParams<{ holdingId: string }>();
  return holdingId ?? "";
}

function TabHint({ children }: { children: React.ReactNode }) {
  return (
    <p className="mb-2 flex items-center gap-1.5 text-xs text-muted-foreground">
      <Info className="h-3.5 w-3.5" /> {children}
    </p>
  );
}

function Empty({ text }: { text: string }) {
  return (
    <div className="rounded-lg border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">{text}</div>
  );
}

function Restricted({ text }: { text: string }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed px-4 py-8 text-center">
      <Lock className="h-5 w-5 text-muted-foreground/50" />
      <span className="text-sm text-muted-foreground">{text}</span>
    </div>
  );
}

function TabLoading() {
  return (
    <div className="space-y-1.5">
      {[0, 1, 2].map((i) => <div key={i} className="h-12 animate-pulse rounded-lg bg-muted" />)}
    </div>
  );
}

/**
 * Этап 4 «Юрособи» — master-detail (LE list | LE detail). The legal entity is a
 * юр/фін level UNDER the holding (NOT a second Client360 card). Selecting a card
 * routes to /legal-entities/:accountId; with no selection the primary/first LE is
 * auto-selected (1-LE collapse). The detail pane carries the ★ F-1 fix (live
 * contacts/outlets slices + bind via the Этап 2 drawer).
 */
export function ClientEntitiesTab() {
  const id = useHoldingId();
  const env = useOutletContext<Client360Envelope>();
  const navigate = useNavigate();
  const { accountId } = useParams<{ accountId?: string }>();
  const q = useClientTab<ClientLegalEntity>(id, "legal-entities", true);
  const [bindOpen, setBindOpen] = useState(false);

  const items = q.data?.items ?? [];
  const legalContourApplicable = (q.data as { legalContourApplicable?: boolean } | undefined)?.legalContourApplicable ?? true;
  const viewable = items.filter((m) => m.canView !== false);
  // Auto-select prefers a READABLE LE (primary first) so the detail pane never
  // fires a 403 on a member outside the manager's scope.
  const selectedId = accountId
    || viewable.find((m) => m.isPrimary)?.id
    || viewable[0]?.id
    || items[0]?.id
    || "";
  const selectedItem = items.find((m) => m.id === selectedId);
  const selectedViewable = selectedItem ? selectedItem.canView !== false : true;
  const clientName = env?.header?.name ?? "Клієнт";

  // Scopes for the bind drawer (Этап 2) — fetched only when the drawer opens.
  const contactsQ = useClient360Contacts(id, {}, bindOpen);
  const scopes = contactsQ.data?.scopes ?? { holding: { id, name: clientName }, legalEntities: [], outlets: [] };

  if (q.isPending) {
    return <div className="p-6 md:p-8"><TabLoading /></div>;
  }
  if (q.data?.restricted) {
    return <div className="p-6 md:p-8"><Restricted text="Перегляд юросіб недоступний" /></div>;
  }
  if (items.length === 0) {
    return (
      <div className="p-6 md:p-8">
        {legalContourApplicable ? (
          <Empty text="Юридичних осіб ще немає." />
        ) : (
          <div className="rounded-lg border border-dashed px-4 py-10 text-center text-sm text-muted-foreground">
            Юридичні особи не застосовуються для цього типу клієнта.
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8">
      <div className="mb-3 flex flex-wrap items-center gap-3">
        {/* BUG3 — surface heading is type-aware (individual → «Дані особи», not «Юрособи»). */}
        <h2 className="mr-auto text-base font-semibold">
          {legalSurfaceLabel(env?.header?.legalIdentityType ?? "", env?.header?.activeMembers ?? items.length)}
          {items.length > 1 && <span className="text-sm font-normal text-muted-foreground"> ({items.length})</span>}
        </h2>
        <button
          onClick={() => navigate(clientRoutes.structure(id))}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted"
        >
          <Network className="h-3.5 w-3.5" /> Відкрити структуру →
        </button>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[260px_1fr]">
        {/* LE list */}
        <div className="space-y-2">
          {items.map((m) => {
            const on = m.id === selectedId;
            return (
              <button
                key={m.id}
                onClick={() => navigate(clientRoutes.legalEntity(id, m.id))}
                className={cn(
                  "w-full rounded-xl border bg-card px-3 py-2.5 text-left transition-colors",
                  on ? "border-primary ring-2 ring-primary/15" : "hover:border-primary/40",
                )}
              >
                <div className="flex items-center gap-1.5">
                  <span className="truncate text-sm font-semibold">{m.name}</span>
                  {m.isPrimary && (
                    <span className="inline-flex shrink-0 items-center gap-0.5 rounded bg-info px-1 py-0.5 text-[10px] font-semibold text-info-foreground">
                      <Star className="h-2.5 w-2.5" />primary
                    </span>
                  )}
                  {!m.active && <span className="shrink-0 rounded bg-muted px-1 py-0.5 text-[10px] font-medium text-muted-foreground">неактивна</span>}
                </div>
                {/* BUG3 — mini-card identity by legalIdentityType: фізособа ≠ «(ФОП)». */}
                <div className="mt-0.5 text-xs text-muted-foreground">
                  {m.legalIdentityType === "individual"
                    ? "Фізична особа"
                    : <>{m.reg}{(m.legalIdentityType === "fop" || (m.legalIdentityType == null && m.isFop)) && <span className="opacity-70"> (ФОП)</span>}</>}
                </div>
                <div className="mt-1.5 flex flex-wrap gap-1">
                  <span className={cn(
                    "rounded px-1.5 py-0.5 text-[10px] font-semibold",
                    m.ar > 0 ? "bg-warning text-warning-foreground" : "bg-success text-success-foreground",
                  )}>
                    {m.ar > 0 ? `борг ${uah(m.ar)}` : "борг 0"}
                  </span>
                  {m.hasActiveContract
                    ? <span className="rounded bg-success px-1.5 py-0.5 text-[10px] font-semibold text-success-foreground">договір active</span>
                    : <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">без договору</span>}
                </div>
              </button>
            );
          })}
        </div>

        {/* LE detail pane */}
        <div className="min-w-0">
          {!selectedId ? (
            <Empty text="Оберіть юрособу зі списку." />
          ) : !selectedViewable ? (
            <Restricted text="Ця юрособа поза вашою зоною доступу. Відкрийте юрособу свого підпорядкування." />
          ) : (
            <LegalEntityDetailPane holdingId={id} accountId={selectedId} onBindContact={() => setBindOpen(true)} />
          )}
        </div>
      </div>

      <ContactDrawer
        open={bindOpen}
        holdingId={id}
        clientName={clientName}
        scopes={scopes}
        clientType={contactsQ.data?.clientType}
        prefillScope={selectedId ? { scope: "legal_entity", targetAccountId: selectedId } : null}
        onClose={() => setBindOpen(false)}
      />
    </div>
  );
}

export function ClientContractsTab() {
  const id = useHoldingId();
  const env = useOutletContext<Client360Envelope>();
  const navigate = useNavigate();
  const q = useClientTab<ClientContract>(id, "contracts", true);
  const canCreate = env?.allowedActions.canCreateContract;
  return (
    <div className="p-6 md:p-8">
      <div className="mb-2 flex items-center justify-between gap-2">
        <TabHint>Договори оформлюються на конкретну юрособу.</TabHint>
        {canCreate && (
          <button
            onClick={() => navigate(clientRoutes.contractNew(id))}
            className="flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium hover:border-primary hover:text-primary"
          >
            + Створити договір
          </button>
        )}
      </div>
      {q.isPending ? <TabLoading /> : q.data?.restricted ? <Restricted text="Перегляд договорів недоступний" /> : (q.data?.items.length ?? 0) === 0 ? (
        <Empty text="Договорів ще немає" />
      ) : (
        <div className="space-y-1.5">
          {q.data!.items.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(clientRoutes.contract(id, c.id))}
              className="flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left hover:border-primary/40"
            >
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">{c.num}</span>
                  {c.source === "sync_1c" && (
                    <span title="Дзеркало з 1С — лише для читання" className="rounded-full bg-info px-1.5 py-0.5 text-[10px] font-medium text-info-foreground">з 1С</span>
                  )}
                  {c.type && <span className="text-xs text-muted-foreground">{c.type}</span>}
                </div>
                <span className="text-xs text-muted-foreground">
                  {c.legalEntityName}{c.validUntil ? ` · до ${c.validUntil}` : ""}
                </span>
              </div>
              {c.status && <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">{c.status}</span>}
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientActivitiesTab() {
  const id = useHoldingId();
  const navigate = useNavigate();
  const q = useClientTab<ClientActivity>(id, "activities", true);
  return (
    <div className="p-6 md:p-8">
      <TabHint>Активності на рівні холдингу.</TabHint>
      {q.isPending ? <TabLoading /> : (q.data?.items.length ?? 0) === 0 ? (
        <Empty text="Активностей ще немає" />
      ) : (
        <div className="space-y-1.5">
          {q.data!.items.map((a) => (
            <button
              key={a.id}
              onClick={() => navigate(clientRoutes.activity(id, a.id.replace(/^[a-z]+:/, "")))}
              className="flex w-full items-center gap-3 rounded-lg border bg-card px-3 py-2.5 text-left hover:border-primary/40"
            >
              <div className="min-w-0 flex-1">
                <span className="text-sm font-medium">{a.title}</span>
                <div className="text-xs text-muted-foreground">{a.kind}{a.when ? ` · ${a.when}` : ""}</div>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/50" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ClientComplaintsTab() {
  const id = useHoldingId();
  const q = useClientTab<ClientComplaint>(id, "complaints", true);
  return (
    <div className="p-6 md:p-8">
      <TabHint>Рекламації холдингу.</TabHint>
      {q.isPending ? <TabLoading /> : q.data?.restricted ? <Restricted text="Перегляд рекламацій недоступний" /> : (q.data?.items.length ?? 0) === 0 ? (
        <Empty text="Активних рекламацій немає" />
      ) : (
        <div className="space-y-1.5">
          {q.data!.items.map((c) => (
            <div key={c.id} className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2.5">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-warning text-warning-foreground">
                <MessageSquareWarning className="h-4 w-4" />
              </div>
              <span className="flex-1 text-sm">{c.title}</span>
              {c.status && <span className="text-xs text-muted-foreground">{c.status}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
